<template>
  <PForm nested :schema="r$" :validate-on="[]" class="availability-form-unavailability-item">
    <div
      class="surface space-y-2 rounded-md p-2"
      :class="{ 'pointer-events-none opacity-50': isDeleting }"
    >
      <div class="grid grid-cols-12 gap-x-2">
        <div class="col-span-6 grid grid-cols-12 gap-x-2">
          <PFormField label="vanaf" class="col-span-8">
            <PInputDate
              v-model="startDate"
              placeholder="DD-MM-YY"
              :disabled="disabled"
              @update:model-value="delayedUpdate.schedule()"
              class="border-gray-lighten-1 rounded-md border border-solid"
            />
          </PFormField>
          <PFormField label="Starttijd" invisible class="col-span-4">
            <PInputTime
              v-model="startTime"
              :date="startDate"
              :disabled="disabled"
              @focusout="delayedUpdate.schedule()"
              class="border-gray-lighten-1 border-solid"
            />
          </PFormField>
        </div>
        <div class="col-span-6 grid grid-cols-12 gap-x-2">
          <PFormField label="tot en met" class="col-span-8">
            <PInputDate
              v-model="endDate"
              placeholder="DD-MM-YY"
              :disabled="disabled || !startDate"
              @update:model-value="delayedUpdate.schedule()"
              class="border-gray-lighten-1 rounded-md border border-solid"
            />
          </PFormField>
          <PFormField label="Eindtijd" invisible class="col-span-4">
            <PInputTime
              v-model="endTime"
              :date="endDate"
              :disabled="disabled || !endDate"
              @focusout="delayedUpdate.schedule()"
              class="border-gray-lighten-1 border-solid"
            />
          </PFormField>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-x-2">
        <div class="col-span-3 flex flex-col space-y-1">
          <PFormField label="Oorzaak" required>
            <PSelect
              v-model="selectedReasonId"
              :items="availabilityReasons"
              value-key="id"
              label-key="label"
              placeholder="Keuze"
              :disabled="disabled"
              @update:model-value="delayedUpdate.schedule()"
              class="border-gray-lighten-1 rounded-md border border-solid"
            >
              <template #default>{{ modelValue.reason?.label ?? "Keuze" }}</template>
            </PSelect>
          </PFormField>

          <div>
            <PButton
              :icon="['fas', 'trash-can']"
              title="Afwezigheid verwijderen"
              :disabled="disabled"
              size="sm"
              @click="onClickDelete"
              >Verwijderen</PButton
            >
          </div>
        </div>

        <div class="col-span-9">
          <PFormField label="Opmerking">
            <PTextarea
              v-model="modelValue.description"
              placeholder="Verdere toelichting"
              :disabled="disabled"
              @input="delayedUpdate.schedule()"
              class="border-gray-lighten-1 border-solid"
              autoresize
            />
          </PFormField>
        </div>
      </div>
      <PAlertFormValidation :model-value="r$.range" />
    </div>
  </PForm>
</template>

<script setup lang="ts">
import PForm from "@/components/form/PForm.vue"
import PAlertFormValidation from "@/components/form/PAlertFormValidation.vue"
import { computed, inject, ref, watch } from "vue"
import PFormField from "@/components/form/PFormField.vue"
import { useRegle, markStatic } from "@regle/core"
import type { Moment } from "moment"
import PButton from "@/components/elements/PButton.vue"
import PInputDate from "@/components/form/PInputDate.vue"
import PSelect from "@/components/form/PSelect.vue"
import PTextarea from "@/components/form/PTextarea.vue"
import PInputTime from "@/components/form/PInputTime.vue"
import type { AvailabilityBlock } from "@/models/availability-block.model.ts"
import type { AvailabilityReason } from "@/models/availability-reason.model.ts"
import {
  getDatePortion,
  getTimePortion,
  setDatePortion,
  setTimePortion
} from "@/helper/time-helper.ts"
import type { AvailabilityBlockController } from "@/controllers/model-controllers/availability-block-controller.ts"
import useEntityUpdater from "@/composables/api/entity-updater.ts"
import useDelayedFunction from "@/composables/input/delayed-function.ts"
import { chronologicalStartAndEndTimeValidator } from "@/helper/validation/time-validation.ts"

const modelValue = defineModel<AvailabilityBlock>({ required: true })

const props = withDefaults(
  defineProps<{
    availabilityReasons: AvailabilityReason[]
    disabled?: boolean
  }>(),
  {
    disabled: false
  }
)

const selectedReasonId = computed({
  get: () => modelValue.value.reason?.id,
  set: (id) => {
    modelValue.value.reason = props.availabilityReasons.find((reason) => reason.id === id) ?? null
  }
})

const emit = defineEmits<{
  hasError: [id: string, value: boolean]
  save: []
}>()

const availabilityBlockController = inject<AvailabilityBlockController>(
  "availabilityBlockController"
)!
const entityUpdater = useEntityUpdater(
  modelValue,
  availabilityBlockController.update,
  availabilityBlockController.create
)
const delayedUpdate = useDelayedFunction(() => {
  if (r$.range?.$invalid) {
    return
  }
  entityUpdater.update()
  emit("save")
}, 300)

const isDeleting = ref(false)
function onClickDelete() {
  isDeleting.value = true
  availabilityBlockController.delete(modelValue.value)
}

// Split timeStart into separate date and time computed refs for the input controls.
const startDate = computed<Moment | null>({
  get: () => getDatePortion(modelValue.value.timeStart),
  set: (value) => (modelValue.value.timeStart = setDatePortion(modelValue.value.timeStart, value))
})

const startTime = computed<Moment | null>({
  get: () => getTimePortion(modelValue.value.timeStart),
  set: (value) => (modelValue.value.timeStart = setTimePortion(modelValue.value.timeStart, value))
})

// Split timeEnd into separate date and time computed refs for the input controls.
const endDate = computed<Moment | null>({
  get: () => getDatePortion(modelValue.value.timeEnd),
  set: (value) => (modelValue.value.timeEnd = setDatePortion(modelValue.value.timeEnd, value))
})

const endTime = computed<Moment | null>({
  get: () => getTimePortion(modelValue.value.timeEnd),
  set: (value) => (modelValue.value.timeEnd = setTimePortion(modelValue.value.timeEnd, value))
})

// --- Validation ---

const validationRules = {
  range: {
    timeRange: chronologicalStartAndEndTimeValidator({
      allowEqual: true,
      treatMidnightAsEndOfDay: true
    })
  }
}

const validationState = computed(() => ({
  range: markStatic({ timeStart: modelValue.value.timeStart, timeEnd: modelValue.value.timeEnd })
}))

const { r$ } = useRegle(validationState, validationRules, { autoDirty: false })

watch(
  () => r$.range?.$invalid,
  (value) => {
    if (modelValue.value.id) {
      emit("hasError", modelValue.value.id, value)
    }
  }
)
</script>

<style scoped></style>
